import { Button, Card, DisabledReason, NumberField, Select, STATUS_TONES } from '@hgc/ui';
import { modeNumber, type VehicleKind } from '@hgc/ardupilot';
import { MAV_FRAME_GLOBAL_RELATIVE_ALT, vehicleKindOfState, type CommandRequest } from '@hgc/core';
import { enumValue } from '@hgc/mavlink';
import { useState, type JSX, type ReactNode } from 'react';

import { useVehicleState } from '../runtime/hooks';
import { requestOf, useCommandDispatch, type CommandRunner, type ConfirmPrompt } from './commanding';
import { normalizeHeading } from './model';

/**
 * 引导动作：飞到这里、改变高度/航向/速度、绕圈、暂停/继续任务。
 *
 * 这一块的核心不是那几个输入框，而是**每个动作都要能看出"现在能不能用"以及"为什么不能"**。
 * 原因分三层，缺一不可：
 *
 *   1. 输入是否合法（"纬度必须是 -90…90"）；
 *   2. 领域层前置条件（"当前不是引导模式，飞控会直接拒绝这条命令"）——
 *      这一层来自 `@hgc/ardupilot` 的模式表与 `canSwitchMode` 的依据，不是本地拍脑袋的规则；
 *   3. 命令门禁（`CommandManager.check()`）：链路、机型支持、解锁状态、位置估计、危险确认。
 *
 * 三层原因都会**写出来**，而不是只把按钮变灰。
 */

export interface GuidedProps {
  readonly runner: CommandRunner;
}

/** 还没被用户碰过的输入跟随实时读数；一旦编辑过就固定住用户的值。 */
function useField(fallback: number | null): [number | null, (value: number | null) => void] {
  const [edited, setEdited] = useState<number | null | undefined>(undefined);
  return [edited === undefined ? fallback : edited, setEdited];
}

const ORBIT_YAW_HOLD_CENTER =
  enumValue('ORBIT_YAW_BEHAVIOUR', 'ORBIT_YAW_BEHAVIOUR_HOLD_FRONT_TO_CIRCLE_CENTER') ?? 0;

export function GuidedPanel({ runner }: GuidedProps): JSX.Element {
  const state = useVehicleState();
  const dispatch = useCommandDispatch(runner);
  const kind: VehicleKind = state === null ? 'unknown' : vehicleKindOfState(state);

  const currentLat = state?.latDeg ?? null;
  const currentLon = state?.lonDeg ?? null;
  const currentAlt = state?.relativeAltM ?? null;
  // 航向优先用 GLOBAL_POSITION_INT 的罗盘航向（已经是 0…360），退回 ATTITUDE 的偏航角并归一化
  const currentHeading =
    state?.headingDeg ?? (state?.yawDeg === null || state?.yawDeg === undefined ? null : normalizeHeading(state.yawDeg));
  const currentSpeed = state?.groundspeedMps ?? null;
  /**
   * 目标速度的默认值：机体当前地速；机体静止时退回飞控自己的默认速度。
   * `WPNAV_SPEED` 的出厂值是 500 cm/s，也就是 5 m/s —— 用一个"和飞控一致"的数
   * 而不是 0，是因为 0 在这里是非法值（填 0 等于不填，表单会立刻报错）。
   */
  const speedFallback = currentSpeed !== null && currentSpeed > 0 ? currentSpeed : 5;

  const [gotoLat, setGotoLat] = useField(currentLat);
  const [gotoLon, setGotoLon] = useField(currentLon);
  const [gotoAlt, setGotoAlt] = useField(currentAlt);
  const [gotoSpeed, setGotoSpeed] = useField(0);

  const [altitude, setAltitude] = useField(currentAlt);
  const [heading, setHeading] = useField(currentHeading);
  const [speed, setSpeed] = useField(speedFallback);
  const [radius, setRadius] = useField(20);
  const [turns, setTurns] = useField(1);
  const [orbitClockwise, setOrbitClockwise] = useState(true);

  const currentMode = state?.customMode ?? null;
  const guidedMode = modeNumber(kind, 'GUIDED');
  const autoMode = modeNumber(kind, 'AUTO');
  const inGuided = guidedMode !== undefined && currentMode === guidedMode;
  const inAuto = autoMode !== undefined && currentMode === autoMode;
  const modeLabel = state?.modeName ?? '未知模式';

  /*
   * 三条前置条件，各自都能说清"为什么不满足就发不出去"：
   *
   * 1. **引导模式**：ArduCopter 的引导命令入口（重定位、环绕、条件偏航等）只在引导模式
   *    的处理函数里，别的模式下飞控只会回一个 `DENIED`，而 `DENIED` 不带原因 ——
   *    用户拿到的是一句"被拒绝"，查不出所以然。所以这条前提必须在界面上先讲清楚。
   * 2. **已解锁**：同一个入口要求电机已上电（未解锁的机体不会执行引导动作）。同样地，
   *    飞控不会解释这件事。
   * 3. **自动模式**：暂停/继续是任务流程的一部分，只对正在跑的航线有意义。
   *
   * 这三条是**本地补充**的前置条件，不算在 `CommandManager` 的门禁里：门禁只管协议层面
   * 能判定的东西（链路、机型支持、解锁要求、位置估计）。放在这里是因为它们属于
   * "这个动作在这个机型上需要什么"，而那份知识属于界面要展示的领域信息。
   */
  const guidedReason = inGuided
    ? undefined
    : `当前模式是「${modeLabel}」，不是引导（GUIDED）模式：飞控会直接拒绝这条命令。` +
      '先切到引导模式，或者直接起飞（起飞会自动进入引导模式）。';

  const autoReason = inAuto
    ? undefined
    : `当前模式是「${modeLabel}」，不在自动（AUTO）任务模式下：暂停/继续只对正在执行的任务有效。`;

  const clearance =
    state === null
      ? '还没有收到机体状态：先连接链路并等第一帧心跳。'
      : state.armed
        ? undefined
        : '尚未解锁：引导类动作要在电机解锁后才会被执行（未解锁时机体不会动），请先解锁。';

  const gotoReason =
    gotoLat === null || gotoLon === null || gotoAlt === null
      ? '请填写完整的纬度、经度与相对高度（可以从机体当前位置改）'
      : Math.abs(gotoLat) > 90 || Math.abs(gotoLon) > 180
        ? '坐标超出范围：纬度 -90…90、经度 -180…180'
        : (guidedReason ?? clearance);

  const gotoRequest: CommandRequest | null =
    gotoLat === null || gotoLon === null || gotoAlt === null
      ? null
      : requestOf('MAV_CMD_DO_REPOSITION', [
          gotoSpeed ?? 0,
          0,
          0,
          Number.NaN,
          gotoLat,
          gotoLon,
          gotoAlt,
        ]);

  const altitudeReason = altitude === null ? '请填写目标相对高度' : (guidedReason ?? clearance);
  const altitudeRequest: CommandRequest | null =
    altitude === null
      ? null
      : {
          command: 'MAV_CMD_DO_CHANGE_ALTITUDE',
          params: [altitude, MAV_FRAME_GLOBAL_RELATIVE_ALT],
          altitudeFrame: 'relative',
        };

  const headingReason = heading === null ? '请填写目标航向（0…360 度）' : (guidedReason ?? clearance);
  const headingRequest: CommandRequest | null =
    heading === null ? null : requestOf('MAV_CMD_CONDITION_YAW', [heading, 30, 1, 0]);

  const speedReason =
    speed === null || speed <= 0 ? '请填写大于 0 的目标速度（m/s）' : (guidedReason ?? clearance);
  const speedRequest: CommandRequest | null =
    speed === null || speed <= 0 ? null : requestOf('MAV_CMD_DO_CHANGE_SPEED', [1, speed, -1]);

  const orbitReason =
    radius === null || radius <= 0
      ? '半径必须大于 0 米（负半径在上游表示逆时针，这里用"方向"选择）'
      : (guidedReason ?? clearance);
  const orbitRequest: CommandRequest | null =
    radius === null || radius <= 0
      ? null
      : requestOf('MAV_CMD_DO_ORBIT', [
          orbitClockwise ? radius : -radius,
          Number.NaN,
          ORBIT_YAW_HOLD_CENTER,
          (turns ?? 0) * 2 * Math.PI,
          Number.NaN,
          Number.NaN,
          Number.NaN,
        ]);

  const continueRequest = requestOf('MAV_CMD_DO_PAUSE_CONTINUE', [1]);
  const pauseRequest = requestOf('MAV_CMD_DO_PAUSE_CONTINUE', [0]);

  const dispatchGuided = (
    request: CommandRequest | null,
    reason: string | undefined,
    prompt: ConfirmPrompt,
    anchor: HTMLElement,
  ): void => {
    if (request === null || reason !== undefined) return;
    void dispatch(request, prompt, anchor);
  };

  return (
    <Card
      title="引导动作"
      subtitle="所有动作都走命令管理器；每个动作都会说明它现在能不能用、以及为什么不能"
    >
      <div className="flex flex-col gap-3">
        <GuidedRow
          id="goto"
          title="飞到这里"
          description="引导机体飞到指定坐标（DO_REPOSITION）。速度填 0 表示用飞控默认速度。"
          actionLabel="引导到此处"
          request={gotoRequest}
          invalidReason={
            gotoLat === null || gotoLon === null || gotoAlt === null
              ? '请填写完整的纬度、经度与相对高度'
              : Math.abs(gotoLat) > 90 || Math.abs(gotoLon) > 180
                ? '坐标超出范围：纬度 -90…90、经度 -180…180'
                : undefined
          }
          domainReason={guidedReason ?? clearance}
          runner={runner}
          fields={[
            {
              id: 'lat',
              label: '纬度',
              unit: '°',
              value: gotoLat,
              decimals: 6,
              onChange: setGotoLat,
            },
            {
              id: 'lon',
              label: '经度',
              unit: '°',
              value: gotoLon,
              decimals: 6,
              onChange: setGotoLon,
            },
            {
              id: 'alt',
              label: '高度',
              unit: 'm',
              value: gotoAlt,
              decimals: 1,
              onChange: setGotoAlt,
            },
            {
              id: 'speed',
              label: '速度',
              unit: 'm/s',
              value: gotoSpeed,
              decimals: 1,
              min: 0,
              onChange: setGotoSpeed,
            },
          ]}
          onAction={(anchor) =>
            dispatchGuided(
              gotoRequest,
              gotoReason,
              {
                title: '引导到该坐标？',
                consequence: `机体将立刻飞向 ${gotoLat?.toFixed(6) ?? '--'}, ${gotoLon?.toFixed(6) ?? '--'}，目标相对高度 ${gotoAlt?.toFixed(1) ?? '--'} 米。下发后无法撤回，只能再发一条新指令改变目标。`,
                confirmLabel: '立即飞往该点',
                danger: 'warning',
              },
              anchor,
            )
          }
        />

        <GuidedRow
          id="altitude"
          title="改变高度"
          description="改变目标相对高度（DO_CHANGE_ALTITUDE），高度基准为相对起飞点。"
          actionLabel="改变高度"
          request={altitudeRequest}
          invalidReason={altitude === null ? '请填写目标相对高度' : undefined}
          domainReason={guidedReason ?? clearance}
          runner={runner}
          fields={[
            {
              id: 'altitude',
              label: '目标高度',
              unit: 'm',
              value: altitude,
              decimals: 1,
              onChange: setAltitude,
            },
          ]}
          onAction={(anchor) =>
            dispatchGuided(
              altitudeRequest,
              altitudeReason,
              {
                title: '改变高度？',
                consequence: `机体将立刻开始爬升或下降到相对高度 ${altitude?.toFixed(1) ?? '--'} 米。下发后无法撤回。`,
                confirmLabel: '立即改变高度',
                danger: 'warning',
              },
              anchor,
            )
          }
        />

        <GuidedRow
          id="heading"
          title="改变航向"
          description="让机体转到指定航向（CONDITION_YAW），30 度/秒、顺时针、绝对角度。"
          actionLabel="改变航向"
          request={headingRequest}
          invalidReason={heading === null ? '请填写目标航向（0…360 度）' : undefined}
          domainReason={guidedReason ?? clearance}
          runner={runner}
          fields={[
            { id: 'heading', label: '目标航向', unit: '°', value: heading, decimals: 0, onChange: setHeading },
          ]}
          onAction={(anchor) =>
            dispatchGuided(
              headingRequest,
              headingReason,
              {
                title: '改变航向？',
                consequence: `机体将立刻顺时针转到航向 ${heading?.toFixed(0) ?? '--'} 度。下发后无法撤回。`,
                confirmLabel: '立即转向',
                danger: 'warning',
              },
              anchor,
            )
          }
        />

        <GuidedRow
          id="speed"
          title="改变速度"
          description="改变水平（地速）目标速度（DO_CHANGE_SPEED），油门保持不变。"
          actionLabel="改变速度"
          request={speedRequest}
          invalidReason={speed === null || speed <= 0 ? '请填写大于 0 的目标速度' : undefined}
          domainReason={guidedReason ?? clearance}
          runner={runner}
          fields={[
            {
              id: 'speed',
              label: '目标速度',
              unit: 'm/s',
              value: speed,
              decimals: 1,
              min: 0,
              onChange: setSpeed,
            },
          ]}
          onAction={(anchor) =>
            dispatchGuided(
              speedRequest,
              speedReason,
              {
                title: '改变速度？',
                consequence: `机体将立刻把目标地速改为 ${speed?.toFixed(1) ?? '--'} m/s。下发后无法撤回。`,
                confirmLabel: '立即改变速度',
                danger: 'warning',
              },
              anchor,
            )
          }
        />

        <GuidedRow
          id="orbit"
          title="绕圈"
          description="绕当前位置做圆周飞行（DO_ORBIT）。圈数填 0 表示一直绕，直到收到停止指令。"
          actionLabel="开始绕圈"
          request={orbitRequest}
          invalidReason={radius === null || radius <= 0 ? '半径必须大于 0 米' : undefined}
          domainReason={guidedReason ?? clearance}
          runner={runner}
          fields={[
            {
              id: 'radius',
              label: '半径',
              unit: 'm',
              value: radius,
              decimals: 0,
              min: 0,
              onChange: setRadius,
            },
            { id: 'turns', label: '圈数（0=一直绕）', unit: '圈', value: turns, decimals: 1, min: 0, onChange: setTurns },
          ]}
          extraFields={
            <Select
              aria-label="绕圈方向"
              size="sm"
              value={orbitClockwise ? 'cw' : 'ccw'}
              options={[
                { value: 'cw', label: '顺时针' },
                { value: 'ccw', label: '逆时针' },
              ]}
              onChange={(value) => setOrbitClockwise(value === 'cw')}
            />
          }
          onAction={(anchor) =>
            dispatchGuided(
              orbitRequest,
              orbitReason,
              {
                title: '开始绕圈？',
                consequence: `机体将立刻以 ${radius?.toFixed(0) ?? '--'} 米为半径、${orbitClockwise ? '顺时针' : '逆时针'}绕当前位置飞行。下发后无法撤回，只能再发一条新指令结束它。`,
                confirmLabel: '立即开始绕圈',
                danger: 'warning',
              },
              anchor,
            )
          }
        />

        <GuidedRow
          id="mission"
          title="暂停 / 继续任务"
          description="暂停正在执行的航线，或者从暂停处继续（DO_PAUSE_CONTINUE）。"
          actionLabel="继续任务"
          request={continueRequest}
          invalidReason={undefined}
          domainReason={autoReason ?? clearance}
          runner={runner}
          fields={[]}
          extraButtons={
            <DisabledReason reason={autoReason ?? clearance ?? runner.disabledReason(pauseRequest)}>
              <Button
                variant="secondary"
                size="sm"
                onClick={(event) => {
                  void dispatch(
                    pauseRequest,
                    {
                      title: '暂停任务？',
                      consequence: '机体将立刻停在当前航点上（悬停），任务进度保持不变，之后可以继续。',
                      confirmLabel: '立即暂停',
                      danger: 'warning',
                    },
                    event.currentTarget,
                  );
                }}
              >
                暂停任务
              </Button>
            </DisabledReason>
          }
          onAction={(anchor) =>
            dispatchGuided(
              continueRequest,
              autoReason ?? clearance,
              {
                title: '继续任务？',
                consequence: '机体将立刻从当前航点继续执行航线，任务进度不会再停在这里。',
                confirmLabel: '立即继续',
                danger: 'warning',
              },
              anchor,
            )
          }
        />
      </div>

      <p className="m-0 mt-3 text-[10px] leading-relaxed text-fg-subtle">
        引导类命令只在引导（GUIDED）模式下被飞控处理；暂停/继续属于任务流程，只在自动（AUTO）模式下有效。
        这里把这条前提明确显示出来，而不是让用户下发一次、等飞控回一个 DENIED 才知道。
      </p>
    </Card>
  );
}

interface FieldSpec {
  readonly id: string;
  readonly label: string;
  readonly unit: string;
  readonly value: number | null;
  readonly decimals: number;
  readonly min?: number;
  readonly onChange: (value: number | null) => void;
}

interface GuidedRowProps {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly actionLabel: string;
  readonly request: CommandRequest | null;
  /** 输入本身不合法（与"现在不能用"无关） */
  readonly invalidReason: string | undefined;
  /** 领域层的前置条件 */
  readonly domainReason: string | undefined;
  readonly runner: CommandRunner;
  readonly fields: readonly FieldSpec[];
  readonly extraFields?: JSX.Element;
  readonly extraButtons?: ReactNode;
  readonly onAction: (anchor: HTMLElement) => void;
}

function GuidedRow({
  id,
  title,
  description,
  actionLabel,
  request,
  invalidReason,
  domainReason,
  runner,
  fields,
  extraFields,
  extraButtons,
  onAction,
}: GuidedRowProps): JSX.Element {
  const runnerReason =
    invalidReason ?? domainReason ?? (request === null ? undefined : runner.disabledReason(request));
  const blocked = runnerReason !== undefined;

  return (
    <section
      data-hgc-guided={id}
      data-hgc-guided-blocked={blocked ? 'true' : 'false'}
      className="flex flex-col gap-2 rounded-control border border-line bg-surface-2 p-2"
    >
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="m-0 text-xs font-semibold text-fg">{title}</p>
          <p className="m-0 mt-0.5 text-[10px] text-fg-subtle">{description}</p>
        </div>
        <DisabledReason reason={runnerReason}>
          <Button
            variant="secondary"
            size="sm"
            onClick={(event) => {
              if (blocked) return;
              onAction(event.currentTarget);
            }}
          >
            {actionLabel}
          </Button>
        </DisabledReason>
        {extraButtons === undefined ? null : extraButtons}
      </div>

      {fields.length === 0 && extraFields === undefined ? null : (
        <div className="flex flex-wrap items-end gap-2">
          {fields.map((field) => (
            <label key={field.id} className="flex flex-col gap-0.5 text-[10px] text-fg-subtle">
              {field.label}
              <NumberField
                aria-label={`${title} · ${field.label}`}
                size="sm"
                value={field.value}
                unit={field.unit}
                decimals={field.decimals}
                {...(field.min === undefined ? {} : { min: field.min })}
                showStepper
                onChange={field.onChange}
              />
            </label>
          ))}
          {extraFields}
        </div>
      )}

      {runnerReason === undefined ? null : (
        <p
          data-hgc-guided-reason=""
          className={`m-0 rounded-control border-l-4 px-2 py-1 text-[10px] font-semibold ${STATUS_TONES.caution.borderLeft} ${STATUS_TONES.caution.surface} text-fg`}
        >
          {runnerReason}
        </p>
      )}
    </section>
  );
}
